import { KPIResult, TargetType, VariableType } from '@/lib/calculator';
import { formatCurrency, formatNumber, formatPercent } from '@/lib/utils';
import { format } from 'date-fns';
import { ClipboardList } from 'lucide-react';

interface ResultSummaryProps {
  selectedResult: KPIResult | null;
  target: TargetType;
  getValue: (type: VariableType | 'amortizationYears' | 'debtServiceRatio') => number;
}

export function ResultSummary({ selectedResult, target, getValue }: ResultSummaryProps) {
  if (!selectedResult) return null;

  // Use the original formatDate function from ResultsGrid
  const formatDate = (date: Date | number): string => {
    return format(new Date(date), 'MMMM d, yyyy');
  };

  const getResultSummary = (result: KPIResult): string => {
    if (!result) return '';

    const monthlySavings = getValue('savings');
    const targetDate = new Date(getValue('date'));
    const annualIncome = getValue('income');
    const targetPrice = getValue('price');

    const mortgageInsuranceWarning = (result.downPaymentPercentage < 20) 
      ? " Since the down payment is less than 20%, mortgage insurance may be required."
      : "";

    const debtServiceRatio = getValue('debtServiceRatio');

    const mortgageInfo = result.mortgageAmount > 0
      ? `With an annual income of ${formatCurrency(annualIncome)} (${formatNumber(debtServiceRatio)}% debt service ratio), you may qualify for a ${formatCurrency(result.mortgageAmount)} mortgage at ${formatNumber(result.interestRate)}% for ${getValue('amortizationYears')} years with a ${formatCurrency(result.monthlyPayment)} monthly payment.`
      : `With an annual income of ${formatCurrency(annualIncome)}, no mortgage would be needed as the purchase could be made with cash.`;

    const getTimeDescription = (months: number): string => {
      const years = Math.floor(months / 12);
      const remainingMonths = months % 12;
      if (years > 0) {
        return remainingMonths > 0 
          ? `${years} year${years > 1 ? 's' : ''} and ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`
          : `${years} year${years > 1 ? 's' : ''}`;
      }
      return `${months} month${months > 1 ? 's' : ''}`;
    };

    const getDownPaymentInfo = () => {
      // Shorten when no additional savings are needed
      if (result.savingPeriodMonths === 0 || result.additionalSavings === 0) {
        return `A down payment of ${formatCurrency(result.downPayment)} (${formatPercent(result.downPaymentPercentage)} of the purchase price) from your current savings.${mortgageInsuranceWarning}`;
      }

      const timeDescription = getTimeDescription(result.savingPeriodMonths);
      return `A down payment of ${formatCurrency(result.downPayment)} (${formatPercent(result.downPaymentPercentage)} of the purchase price) could be achieved by combining current savings of ${formatCurrency(result.initialDownPayment)} plus ${formatCurrency(result.additionalSavings)} from monthly savings over ${timeDescription}.${mortgageInsuranceWarning}`;
    };

    switch (target) {
      case 'savings':
        if (result.requiredSavings === Infinity) {
          return `It may not be possible to purchase a ${formatCurrency(targetPrice)} home by ${formatDate(targetDate)} with the current down payment. Consider a later purchase date or increasing the current down payment.`;
        }

        if (result.requiredSavings === 0) {
          if (result.mortgageAmount > 0) {
            return `To purchase a ${formatCurrency(targetPrice)} home by ${formatDate(targetDate)}, no additional monthly savings are needed. Your current savings of ${formatCurrency(result.initialDownPayment)} are sufficient for the down payment. ${mortgageInfo} ${getDownPaymentInfo()}`;
          } else {
            return `You can purchase a ${formatCurrency(targetPrice)} home by ${formatDate(targetDate)} with no additional savings needed. With your current savings of ${formatCurrency(result.initialDownPayment)}, no mortgage would be needed as the purchase could be made with cash.`;
          }
        }
        
        const timeDescription = getTimeDescription(result.savingPeriodMonths);
        const savingsChange = result.requiredSavings > monthlySavings
          ? `This would require increasing monthly savings from ${formatCurrency(monthlySavings)} to ${formatCurrency(result.requiredSavings)}`
          : `Monthly savings could be reduced from ${formatCurrency(monthlySavings)} to ${formatCurrency(result.requiredSavings)}`;
        
        return `To purchase a ${formatCurrency(targetPrice)} home by ${formatDate(targetDate)}, a monthly savings of ${formatCurrency(result.requiredSavings)} may be needed. ${savingsChange} to accumulate ${formatCurrency(result.additionalSavings)} over ${timeDescription}. ${mortgageInfo} ${getDownPaymentInfo()}`;

      case 'date':
        if (!result.purchaseDate) {
          if (monthlySavings <= 0) {
            return `With no monthly savings, it may not be possible to afford a ${formatCurrency(targetPrice)} home. Consider increasing your savings rate or current down payment.`;
          }
          return `It may not be possible to afford a ${formatCurrency(targetPrice)} home with the current savings rate of ${formatCurrency(monthlySavings)}/month. Consider increasing your savings rate or current down payment.`;
        }
        return `You could potentially purchase a ${formatCurrency(targetPrice)} home by ${formatDate(result.purchaseDate)}. ${mortgageInfo} ${getDownPaymentInfo()}`;
      
      case 'payment':
        return `Your monthly mortgage payment would be ${formatCurrency(result.monthlyPayment)} for a ${formatCurrency(targetPrice)} home with a ${formatCurrency(result.downPayment)} down payment. ${mortgageInfo} ${getDownPaymentInfo()}`;
        
      default:
        const totalPrice = result.mortgageAmount + result.downPayment;
        return `You could potentially afford a ${formatCurrency(totalPrice)} home by ${formatDate(targetDate)}. ${mortgageInfo} ${getDownPaymentInfo()}`;
    }
  };

  return (
    <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200 shadow-sm mb-4">
      <div className="flex items-center gap-3 mb-2">
        <ClipboardList className="w-6 h-6 text-primary" />
        <h3 className="font-semibold text-gray-900">Summary</h3>
      </div>
      <p className="text-gray-600">
        {getResultSummary(selectedResult)}
      </p>
    </div>
  );
}